import { createContext, useContext, ReactNode, useMemo } from 'react'
import { toast } from 'sonner'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

interface NotificationContextType {
  showNotification: (type: ToastType, title: string, message?: string, duration?: number) => void
  showSuccess: (title: string, message?: string) => void
  showError: (title: string, message?: string) => void
  showWarning: (title: string, message?: string) => void
  showInfo: (title: string, message?: string) => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

interface NotificationProviderProps {
  children: ReactNode
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const value = useMemo<NotificationContextType>(() => {
    const showNotification: NotificationContextType['showNotification'] = (type, title, message, duration) => {
      const options = { description: message, duration }
      switch (type) {
        case 'success':
          toast.success(title, options)
          break
        case 'error':
          toast.error(title, { ...options, duration: duration ?? 7000 })
          break
        case 'warning':
          toast.warning(title, options)
          break
        case 'info':
        default:
          toast.info(title, options)
          break
      }
    }

    return {
      showNotification,
      showSuccess: (title, message) => showNotification('success', title, message),
      showError: (title, message) => showNotification('error', title, message),
      showWarning: (title, message) => showNotification('warning', title, message),
      showInfo: (title, message) => showNotification('info', title, message),
    }
  }, [])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}
